import { useState, useEffect, useRef } from "react";
import { Github, Linkedin, ExternalLink, ChevronDown, Menu, X, Cloud, Code2, Brain } from "lucide-react";

// ─── Global Styles ────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:         #0A0A0A;
    --surface:    #111111;
    --border:     rgba(255,255,255,0.07);
    --text:       #F0F0F0;
    --muted:      #666666;
    --accent:     #00E5FF;
    --accent-dim: rgba(0,229,255,0.07);
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    cursor: none;
  }

  /* ── Cursor ── */
  .cur-dot {
    width: 4px; height: 4px;
    background: var(--accent);
    border-radius: 50%;
    position: fixed;
    top: 0; left: 0;
    pointer-events: none;
    z-index: 9999;
    transform: translate(-50%, -50%);
  }
  .cur-ring {
    width: 28px; height: 28px;
    border: 1px solid rgba(0,229,255,0.3);
    border-radius: 50%;
    position: fixed;
    top: 0; left: 0;
    pointer-events: none;
    z-index: 9998;
    transform: translate(-50%, -50%);
    transition: width 0.18s ease, height 0.18s ease, border-color 0.18s ease;
  }
  .cur-ring.hovered {
    width: 44px; height: 44px;
    border-color: rgba(0,229,255,0.6);
  }
  a, button { cursor: none; }

  ::-webkit-scrollbar { width: 1px; }
  ::-webkit-scrollbar-thumb { background: var(--accent); }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .fu { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) both; }
  .fi { animation: fadeIn 0.7s ease both; }
  .d1 { animation-delay: 0.1s; }
  .d2 { animation-delay: 0.25s; }
  .d3 { animation-delay: 0.45s; }
  .d4 { animation-delay: 0.65s; }
  .d5 { animation-delay: 0.85s; }

  .reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.85s cubic-bezier(0.22,1,0.36,1),
                transform 0.85s cubic-bezier(0.22,1,0.36,1);
  }
  .reveal.in { opacity: 1; transform: translateY(0); }

  .serif { font-family: 'Playfair Display', Georgia, serif; }

  .nav-a {
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--muted);
    text-decoration: none;
    transition: color 0.2s;
  }
  .nav-a:hover { color: var(--text); }

  .chip {
    display: inline-block;
    padding: 4px 12px;
    border: 1px solid var(--border);
    font-size: 0.68rem;
    letter-spacing: 0.06em;
    color: var(--muted);
    transition: all 0.2s;
  }
  .chip:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-dim); }

  .card {
    border: 1px solid var(--border);
    padding: 2rem;
    position: relative;
    background: var(--bg);
    transition: border-color 0.3s, transform 0.3s cubic-bezier(0.22,1,0.36,1);
    overflow: hidden;
  }
  .card::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, var(--accent-dim), transparent);
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  .card:hover { border-color: rgba(0,229,255,0.2); transform: translateY(-3px); }
  .card:hover::after { opacity: 1; }

  .tl { position: relative; padding-left: 1.75rem; }
  .tl::before {
    content: '';
    position: absolute; left: 0; top: 7px;
    width: 5px; height: 5px;
    background: var(--accent);
    border-radius: 50%;
  }
  .tl::after {
    content: '';
    position: absolute; left: 2px; top: 14px;
    width: 1px; height: calc(100% + 2.5rem);
    background: var(--border);
  }
  .tl:last-child::after { display: none; }

  .rule { width: 32px; height: 1px; background: var(--accent); margin: 1.5rem 0; }
  .hl { color: var(--muted); text-decoration: none; transition: color 0.2s; }
  .hl:hover { color: var(--accent); }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const STACK = [
  { cat: "Language", icon: <Code2 size={11}/>, items: ["Python", "SQL", "c++","c"] },
  { cat: "AI / ML",  icon: <Brain  size={11}/>, items: ["PyTorch", "1D CNN", "NLP", "Scikit-learn"] },
  { cat: "Cloud",    icon: <Cloud  size={11}/>, items: ["AWS", "Azure", "Docker",] },
  { cat: "Web",      icon: <Code2  size={11}/>, items: ["Streamlit", "FastAPI", "HTML"] },
];

const PROJECTS = [
  {
    num: "01", title: "Predictive Maintenance", sub: "NASA Turbofan Dataset",
    desc: "1D CNN model for Remaining Useful Life estimation on multivariate jet-engine sensor data. Predicts failure horizons with high accuracy across degradation modes.",
    tags: ["PyTorch", "1D CNN", "Time-Series", "RUL"], stat: "94.2% accuracy", link: "https://github.com/manunaik111/AI_For_Predictive_Maintenance.git",
  },
  {
    num: "02", title: "Hospital Management System", sub: "Healthcare × Administration",
    desc: "A comprehensive digital solution for managing patient records, appointment scheduling, and staff allocation. Streamlines clinical workflows to enhance operational efficiency.",
    tags: ["HTML", "CSS", "JavaScript", "SQL"], stat: "Completed Project", link: "https://github.com/manunaik111/hospital_management_system_5thsem_mini_project.git",
  },
  {
    num: "03", title: " AI Chatbot with Company Data Analysis", sub: "AI × NLP × Data Visualization",
    desc: "Interactive AI chatbot that processes natural language queries, performs data analysis, and generates real-time visual insights using a modular architecture built with Python and Streamlit.",
    tags: ["Python", "Streamlit", "NLP", "Data Analytics", "Visualization"], stat: "Active project", link: "#",
  },
];

const TIMELINE = [
  { period: "2022 – Present", title: "B.E. Computer Science Engineering", place: "AGM Rural College of Engineering and Technology Hubli",    note: "Specialization in AI, Cloud & Data Science" },
  { period: "2026 Feb - Present",    title: "Python-AI Intern",        place: "Genesis Traning",  note: "Building AI Chatbot with Company Data Analysis" },
  /*{ period: "2023",           title: "Independent Research",              place: "AI / ML",       note: "NASA Turbofan predictive maintenance via 1D CNN" },*/
];

// ─── Cursor ───────────────────────────────────────────────────────────────────
function Cursor() {
  const dot  = useRef(null);
  const ring = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const x = e.clientX, y = e.clientY;
      if (dot.current)  { dot.current.style.left  = x + "px"; dot.current.style.top  = y + "px"; }
      if (ring.current) { ring.current.style.left = x + "px"; ring.current.style.top = y + "px"; }
    };

    const onEnter = () => ring.current && ring.current.classList.add("hovered");
    const onLeave = () => ring.current && ring.current.classList.remove("hovered");

    window.addEventListener("mousemove", onMove);

    // Expand ring when hovering links or buttons
    const targets = document.querySelectorAll("a, button");
    targets.forEach(el => { el.addEventListener("mouseenter", onEnter); el.addEventListener("mouseleave", onLeave); });

    return () => {
      window.removeEventListener("mousemove", onMove);
      targets.forEach(el => { el.removeEventListener("mouseenter", onEnter); el.removeEventListener("mouseleave", onLeave); });
    };
  }, []);

  return (
    <>
      <div ref={dot}  className="cur-dot" />
      <div ref={ring} className="cur-ring" />
    </>
  );
}

// ─── Reveal wrapper — fixes "hooks inside callbacks" ESLint error ─────────────
function R({ children, style, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add("in"); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${className}`} style={style}>{children}</div>;
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
function Nav() {
  const [open, setOpen]     = useState(false);
  const [solid, setSolid]   = useState(false);

  useEffect(() => {
    const fn = () => setSolid(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        padding: "1.25rem 2.5rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        transition: "all 0.4s",
        ...(solid ? { background: "rgba(10,10,10,0.92)", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--border)" } : {}),
      }}>
        <div className="serif" style={{ fontSize: "1.1rem" }}>
          M<span style={{ color: "var(--accent)" }}>.</span>
        </div>
        <div style={{ display: "flex", gap: "2.5rem", alignItems: "center" }}>
          {["Stack","Projects","Timeline","Contact"].map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} className="nav-a"
              style={{ display: window.innerWidth < 700 ? "none" : undefined }}>{l}</a>
          ))}
          <button onClick={() => setOpen(o => !o)}
            style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", display: "flex", padding: 0 }}>
            {open ? <X size={16}/> : <Menu size={16}/>}
          </button>
        </div>
      </nav>

      {open && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 40,
          background: "rgba(10,10,10,0.97)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2.5rem",
        }}>
          {["Stack","Projects","Timeline","Contact"].map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setOpen(false)} className="serif"
              style={{ fontSize: "2.5rem", color: "var(--text)", textDecoration: "none", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = "var(--accent)"}
              onMouseLeave={e => e.target.style.color = "var(--text)"}>{l}</a>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: "2rem", position: "relative",
      background: "radial-gradient(ellipse 70% 50% at 50% 40%, rgba(0,229,255,0.03) 0%, transparent 70%)",
    }}>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)",
        backgroundSize: "72px 72px",
      }}/>

      <div style={{ maxWidth: "640px", position: "relative" }}>
        <p className="fi d1" style={{ fontSize: "0.65rem", letterSpacing: "0.3em", color: "var(--accent)", textTransform: "uppercase", marginBottom: "1.75rem" }}>
          Computer Science Engineering
        </p>
        <h1 className="serif fu d2" style={{ fontSize: "clamp(2.6rem, 6vw, 4.8rem)", fontWeight: 700, lineHeight: 1.08, marginBottom: "1.75rem" }}>
          Manu<br/>
          <span style={{ fontStyle: "italic", fontWeight: 400 }}>Engineering the</span><br/>
          Predictive Future<span style={{ color: "var(--accent)" }}>.</span>
        </h1>
        <p className="fu d3" style={{ color: "var(--muted)", fontSize: "0.95rem", lineHeight: 1.85, maxWidth: "440px", margin: "0 auto 3rem" }}>
          CS student focused on AI architectures and cloud infrastructure. Building systems that anticipate before they react.
        </p>
        <div className="fu d4" style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#projects" style={{ padding: "0.7rem 2rem", background: "var(--accent)", color: "#000", fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", textDecoration: "none", fontWeight: 500, transition: "opacity 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>View Work</a>
          <a href="#contact" style={{ padding: "0.7rem 2rem", border: "1px solid var(--border)", color: "var(--muted)", fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", textDecoration: "none", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--muted)"; }}>Contact</a>
        </div>
      </div>

      <div className="fi d5" style={{ position: "absolute", bottom: "2.5rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
        <span style={{ fontSize: "0.58rem", letterSpacing: "0.25em", color: "var(--muted)", textTransform: "uppercase" }}>Scroll</span>
        <ChevronDown size={13} color="var(--muted)"/>
      </div>
    </section>
  );
}

// ─── Stack ────────────────────────────────────────────────────────────────────
function Stack() {
  return (
    <section id="stack" style={{ padding: "8rem 2.5rem" }}>
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <R><span style={{ fontSize: "0.62rem", letterSpacing: "0.25em", color: "var(--accent)", textTransform: "uppercase" }}>01 — Technical Stack</span></R>
        <R style={{ marginTop: "0.75rem", marginBottom: "3.5rem" }}>
          <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700 }}>Built to Build<span style={{ color: "var(--accent)" }}>.</span></h2>
        </R>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1px", border: "1px solid var(--border)", background: "var(--border)" }}>
          {STACK.map((group, i) => (
            <R key={group.cat} style={{ transitionDelay: `${i * 0.08}s`, padding: "1.75rem", background: "var(--bg)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "1.25rem" }}>
                <span style={{ color: "var(--accent)" }}>{group.icon}</span>
                <span style={{ fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--muted)" }}>{group.cat}</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {group.items.map(item => <span key={item} className="chip">{item}</span>)}
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Projects ─────────────────────────────────────────────────────────────────
function Projects() {
  return (
    <section id="projects" style={{ padding: "8rem 2.5rem", background: "var(--surface)" }}>
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <R><span style={{ fontSize: "0.62rem", letterSpacing: "0.25em", color: "var(--accent)", textTransform: "uppercase" }}>02 — Featured Projects</span></R>
        <R style={{ marginTop: "0.75rem", marginBottom: "3.5rem" }}>
          <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700 }}>Selected Work<span style={{ color: "var(--accent)" }}>.</span></h2>
        </R>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1px", border: "1px solid var(--border)", background: "var(--border)" }}>
          {PROJECTS.map((p, i) => (
            <R key={p.num} style={{ transitionDelay: `${i * 0.1}s` }} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                <span style={{ fontSize: "0.6rem", letterSpacing: "0.3em", color: "var(--accent)" }}>{p.num}</span>
                <a href={p.link} className="hl"><ExternalLink size={13}/></a>
              </div>
              <h3 className="serif" style={{ fontSize: "1.35rem", fontWeight: 700, marginBottom: "0.25rem" }}>{p.title}</h3>
              <p style={{ fontSize: "0.68rem", color: "var(--accent)", letterSpacing: "0.1em", marginBottom: "1rem" }}>{p.sub}</p>
              <p style={{ color: "var(--muted)", fontSize: "0.85rem", lineHeight: 1.8, marginBottom: "1.5rem" }}>{p.desc}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "1.5rem" }}>
                {p.tags.map(t => <span key={t} style={{ fontSize: "0.62rem", padding: "3px 10px", border: "1px solid var(--border)", color: "var(--muted)", letterSpacing: "0.06em" }}>{t}</span>)}
              </div>
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                <span style={{ fontSize: "0.68rem", color: "var(--accent)" }}>↗ {p.stat}</span>
              </div>
            </R>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
function Timeline() {
  return (
    <section id="timeline" style={{ padding: "8rem 2.5rem" }}>
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <R><span style={{ fontSize: "0.62rem", letterSpacing: "0.25em", color: "var(--accent)", textTransform: "uppercase" }}>03 — Experience & Education</span></R>
        <R style={{ marginTop: "0.75rem", marginBottom: "4rem" }}>
          <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700 }}>The Path<span style={{ color: "var(--accent)" }}>.</span></h2>
        </R>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6rem", alignItems: "start" }}>
          <R>
            <p style={{ color: "var(--muted)", fontSize: "0.875rem", lineHeight: 1.9, maxWidth: "320px" }}>
              Studying at the intersection of machine learning, cloud infrastructure, and data-driven product thinking.
            </p>
            <div className="rule"/>
            <div style={{ display: "flex", gap: "1.25rem" }}>
              <a href="https://github.com/manunaik111" target="_blank" rel="noreferrer" className="hl"><Github size={15}/></a>
              <a href="https://www.linkedin.com/in/manu-naik-73bb702a7" target="_blank" rel="noreferrer" className="hl"><Linkedin size={15}/></a>
            </div>
          </R>
          <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
            {TIMELINE.map((item, i) => (
              <R key={i} style={{ transitionDelay: `${i * 0.12}s` }} className="tl">
                <span style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "var(--accent)", textTransform: "uppercase", display: "block", marginBottom: "0.35rem" }}>{item.period}</span>
                <h4 className="serif" style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.2rem" }}>{item.title}</h4>
                <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.25rem" }}>{item.place}</p>
                <p style={{ fontSize: "0.78rem", color: "var(--muted)", fontStyle: "italic" }}>{item.note}</p>
              </R>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer id="contact" style={{ borderTop: "1px solid var(--border)", background: "var(--surface)", padding: "6rem 2.5rem 3rem" }}>
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "3rem", alignItems: "end", marginBottom: "5rem" }}>
          <div>
            <span style={{ fontSize: "0.62rem", letterSpacing: "0.25em", color: "var(--accent)", textTransform: "uppercase", display: "block", marginBottom: "1rem" }}>04 — Get In Touch</span>
            <h2 className="serif" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 700, lineHeight: 1.1 }}>
              Let’s design<br/>
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>system that anticipates</span><br/>
              before the world asks<span style={{ color: "var(--accent)" }}>.</span>
            </h2>
          </div>
          <a href="mailto:manupnaik639@gmail.com" style={{ fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--text)", textDecoration: "none", transition: "color 0.2s", whiteSpace: "nowrap" }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--accent)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--text)"}>
            manupnaik639@gmail.com →
          </a>
        </div>
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "2rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
          <span style={{ fontSize: "0.65rem", color: "var(--muted)", letterSpacing: "0.08em" }}>© 2025 Manu</span>
          <div style={{ display: "flex", gap: "1.5rem" }}>
            <a href="https://github.com/manunaik111" target="_blank" rel="noreferrer" className="hl"><Github size={14}/></a>
            <a href="https://www.linkedin.com/in/manu-naik-73bb702a7" target="_blank" rel="noreferrer" className="hl"><Linkedin size={14}/></a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }}/>
      <Cursor/>
      <Nav/>
      <Hero/>
      <Stack/>
      <Projects/>
      <Timeline/>
      <Footer/>
    </>
  );
}
